import {Bindings} from "data/constants/bindings";
import {ModalType, RequestState} from "data/enums";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {IGameBarStore} from "data/stores/game_bar/game_bar.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {IReactionDisposer, makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {Countdown, type IDateTimer, extractErrorMessage} from "data/utils";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IRosterStore} from "data/stores/roster/roster.store";
import {isEmpty} from "lodash";
import {ContestsUtils} from "data/utils/contest.utils";

export interface ILandingController extends ViewController {
	openRegistrationModal: () => void;

	get contestId(): number;

	get i18n(): ILocalizationStore;

	get isReturningUser(): boolean;

	get isTermsAccepted(): boolean;

	get isRosterSubmitted(): boolean;

	get isLoading(): boolean;

	get isDateValid(): boolean;
}

@injectable()
export class LandingController implements ILandingController {
	@observable _requestState = RequestState.IDLE;
	@observable private _subscriptions$: IReactionDisposer[] = [];
	private _timer?: Countdown;
	@observable private _date: IDateTimer | null = null;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.GameBarStore) private readonly _gameBarStore: IGameBarStore,
		@inject(Bindings.ContestsStore) private readonly _contestStore: IContestsStore,
		@inject(Bindings.RoundsStore) private readonly _roundsStore: IRoundsStore,
		@inject(Bindings.RosterStore) private readonly _rosterStore: IRosterStore
	) {
		makeAutoObservable(this);
	}

	get contestId(): number {
		return this.contest?.id ?? 0;
	}

	public get isReturningUser(): boolean {
		return !!this._userStore.ssoDataForRegistration;
	}

	get isTermsAccepted(): boolean {
		return this._userStore.isTermsAccepted;
	}

	private get contest() {
		return this._contestStore.currentContest;
	}

	private get round() {
		return this._roundsStore.activeRound;
	}

	get isRosterSubmitted(): boolean {
		return this._rosterStore.isRosterFull;
	}

	get isLoading(): boolean {
		return [RequestState.IDLE, RequestState.PENDING].includes(this._requestState);
	}

	get isDateValid(): boolean {
		return !!ContestsUtils.toTimeString(this._date);
	}

	public dispose(): void {
		this._subscriptions$.forEach((dispose) => dispose());
	}

	public init(param: void): void {
		const subscription = reaction(
			() => [this.contest, this.isReturningUser],
			() => {
				runInAction(() => {
					this._requestState = RequestState.PENDING;
				});
				void Promise.all([
					this.fetchGameBar(),
					this.fetchRoster()
						.then(this.initTimer)
						.then(() => {
							runInAction(() => {
								this._requestState = RequestState.SUCCESS;
							});
						}),
				]).catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});
					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});
				});
			},
			{fireImmediately: true}
		);

		this._subscriptions$.push(subscription);
	}

	private initTimer = () => {
		this._date = null;
		const isTeamCreated = isEmpty(this._rosterStore.currentSpinnerIds);
		if (!this.contest || isTeamCreated) {
			return;
		}

		this._timer?.clear();
		this._timer = ContestsUtils.initLockTimer(this.contest);
		if (!this._timer) {
			return;
		}

		this._timer
			.onTick((days, hours, minutes, seconds) => {
				runInAction(() => {
					this._date = {
						days,
						hours,
						minutes,
						seconds,
					};
				});
			})
			.onComplete(() => {
				this._date = null;
			})
			.run();
	};

	public onChange(param: void): void {
		return;
	}

	public openRegistrationModal = () => {
		this._modalsStore.showModal(ModalType.SECONDARY_REGISTRATION);
	};

	private get isDisabledAPI() {
		const contest = this.contest;
		return !contest || this.isReturningUser || !this._userStore.isTermsAccepted;
	}

	private async fetchGameBar() {
		if (this.isDisabledAPI) {
			return;
		}

		return this._gameBarStore.fetchGameBar(this.contest!.id);
	}

	private async fetchRoster() {
		const contest = this.contest;

		if (this.isDisabledAPI) {
			return;
		}

		this._rosterStore.updateContestId(contest!.id);
		return this._rosterStore.fetchRoster();
	}
}
